// =============================================
// payrollApi — typed client for the Payroll service (/api/payroll/*).
// Mirrors the kv/music pattern: bearer token from localStorage, JSON in/out.
// Source of truth is PostgreSQL; this service is API-only (no localStorage cache)
// because payroll data is sensitive and must never diverge from the server.
// =============================================
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
function token(): string | null { return localStorage.getItem('akt_auth_token'); }

async function call<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const r = await fetch(`${API_BASE}/payroll${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({} as any));
    const e: any = new Error(body.message || `Request failed (${r.status})`);
    e.code = r.status;
    throw e;
  }
  return r.json();
}

export interface Employee {
  pay_employee_id: number;
  account_owner_id: number;
  user_id?: number | null;
  employee_no?: string;
  first_name: string;
  last_name: string;
  sin?: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  province_of_employment: string;
  hire_date?: string;
  termination_date?: string;
  status: string;
  wage_type: 'hourly' | 'salary';
  hourly_rate?: number;
  annual_salary?: number;
  standard_hours?: number;
  pay_frequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';
  vacation_pct?: number;
  vacation_mode?: 'accrue' | 'payout';
  td1_federal?: number | null;
  td1_provincial?: number | null;
  additional_tax?: number;
  cpp_exempt?: number;
  ei_exempt?: number;
  pay_method?: string;
}

export interface PayRun {
  pay_run_id: number;
  run_no?: string;
  tax_year: number;
  pay_frequency: string;
  period_start: string;
  period_end: string;
  pay_date: string;
  status: 'draft' | 'calculated' | 'approved' | 'posted' | 'cancelled';
  gl_journal_id?: number | null;
  total_gross: number;
  total_deductions: number;
  total_net: number;
  total_employer_cost: number;
  employee_count: number;
}

export interface PayRunLine {
  pay_run_line_id: number;
  pay_employee_id: number;
  first_name: string;
  last_name: string;
  employee_no?: string;
  gross_pay: number;
  cpp_employee: number;
  cpp2_employee: number;
  ei_employee: number;
  federal_tax: number;
  provincial_tax: number;
  other_deductions: number;
  total_deductions: number;
  net_pay: number;
  cpp_employer: number;
  cpp2_employer: number;
  ei_employer: number;
  vacation_accrued: number;
  vacation_paid: number;
  employer_cost: number;
  calc_detail?: any;
  paystub_document_id?: number | null;
}

export const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];
export const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly (52/yr)' },
  { value: 'biweekly', label: 'Bi-weekly (26/yr)' },
  { value: 'semimonthly', label: 'Semi-monthly (24/yr)' },
  { value: 'monthly', label: 'Monthly (12/yr)' },
];

// Employees
export const listEmployees = () => call<Employee[]>('/employees');
export const createEmployee = (e: Partial<Employee>) => call<Employee>('/employees', { method: 'POST', body: JSON.stringify(e) });
export const updateEmployee = (id: number, e: Partial<Employee>) => call<Employee>(`/employees/${id}`, { method: 'PUT', body: JSON.stringify(e) });
export const deleteEmployee = (id: number) => call<{ ok: boolean }>(`/employees/${id}`, { method: 'DELETE' });

// Rates
export const getRates = (year: number) => call<{ year: number; scalars: any[]; brackets: any[] }>(`/rates?year=${year}`);
export const saveScalar = (row: any) => call('/rates/scalar', { method: 'PUT', body: JSON.stringify(row) });
export const saveBracket = (row: any) => call('/rates/bracket', { method: 'PUT', body: JSON.stringify(row) });

// Runs
export const listRuns = () => call<PayRun[]>('/runs');
export const createRun = (r: Partial<PayRun>) => call<PayRun>('/runs', { method: 'POST', body: JSON.stringify(r) });
export const getRun = (id: number) => call<{ run: PayRun; lines: PayRunLine[]; earnings: any[]; deductions: any[] }>(`/runs/${id}`);
export const calculateRun = (id: number, lines?: any[]) => call<any>(`/runs/${id}/calculate`, { method: 'POST', body: JSON.stringify(lines ? { lines } : {}) });
export const approveRun = (id: number) => call<any>(`/runs/${id}/approve`, { method: 'POST' });
export const postRunToGL = (id: number) => call<{ ok: boolean; journal_id: number }>(`/runs/${id}/post-gl`, { method: 'POST' });
export const deleteRun = (id: number) => call<any>(`/runs/${id}`, { method: 'DELETE' });

// Stubs + employee linking
export const generateStubs = (runId: number) => call<{ ok: boolean; generated: number }>(`/runs/${runId}/generate-stubs`, { method: 'POST' });
export const linkEmployeeUser = (employeeId: number, email: string) => call<{ ok: boolean; linked_user_id: number }>(`/employees/${employeeId}/link-user`, { method: 'POST', body: JSON.stringify({ email }) });
/** URL for a line's pay-stub PDF (auth via ?token= since it's opened in a new tab). */
export const stubPdfUrl = (lineId: number) => `${API_BASE}/payroll/lines/${lineId}/stub.pdf?token=${encodeURIComponent(token() || '')}`;
export const eftCsvUrl = (runId: number) => `${API_BASE}/payroll/runs/${runId}/eft.csv?token=${encodeURIComponent(token() || '')}`;
export const t4PdfUrl = (t4Id: number) => `${API_BASE}/payroll/t4/${t4Id}/pdf?token=${encodeURIComponent(token() || '')}`;
export const roePdfUrl = (roeId: number) => `${API_BASE}/payroll/roe/${roeId}/pdf?token=${encodeURIComponent(token() || '')}`;

// Remittances (PD7A)
export const generateRemittance = (period_start: string, period_end: string) =>
  call<any>('/remittances/generate', { method: 'POST', body: JSON.stringify({ period_start, period_end }) });
export const listRemittances = (year: number) => call<any[]>(`/remittances?year=${year}`);
export const markRemittancePaid = (id: number, paid_date: string, confirmation_no?: string) =>
  call<any>(`/remittances/${id}/paid`, { method: 'PUT', body: JSON.stringify({ paid_date, confirmation_no }) });

// Year-end: T4 / ROE
export const generateT4s = (tax_year: number) => call<{ ok: boolean; generated: number }>('/t4/generate', { method: 'POST', body: JSON.stringify({ tax_year }) });
export const listT4s = (year: number) => call<any[]>(`/t4?year=${year}`);
export const generateRoe = (pay_employee_id: number, reason_code?: string, comments?: string) =>
  call<any>('/roe/generate', { method: 'POST', body: JSON.stringify({ pay_employee_id, reason_code, comments }) });
export const listRoes = () => call<any[]>('/roe');

// Timesheets + punches
export const listTimesheets = (period_start?: string, period_end?: string) => {
  const q = new URLSearchParams();
  if (period_start) q.set('period_start', period_start);
  if (period_end) q.set('period_end', period_end);
  return call<any[]>(`/timesheets${q.toString() ? '?' + q.toString() : ''}`);
};
export const saveTimesheet = (t: any) => call<any>('/timesheets', { method: 'POST', body: JSON.stringify(t) });
export const deleteTimesheet = (id: number) => call<any>(`/timesheets/${id}`, { method: 'DELETE' });
export const savePunch = (p: any) => call<any>('/punches', { method: 'POST', body: JSON.stringify(p) });
export const rollupPunches = (pay_employee_id: number, period_start: string, period_end: string, ot_threshold?: number) =>
  call<any>('/punches/rollup', { method: 'POST', body: JSON.stringify({ pay_employee_id, period_start, period_end, ot_threshold }) });
export const extractTimesheet = (base64: string, mime: string) =>
  call<{ rows: { employee_name: string; regular_hours: number; overtime_hours: number; note?: string }[] }>('/timesheets/extract', { method: 'POST', body: JSON.stringify({ base64, mime }) });
export const runTimesheetHours = (runId: number) => call<{ pay_employee_id: number; regular_hours: number; overtime_hours: number }[]>(`/runs/${runId}/timesheet-hours`);
